import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import api from '../../../services/api';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import PersonCell from '../../../components/ui/PersonCell';

// ProbationReviewDataAccess.ValidOutcomes ve model yorumundaki durum makinesiyle AYNI kümeler.
// Backend tek gerçek kaynaktır; burada yalnız KOPYALANIR (HA_RULE_CATALOG ile aynı ilke).
export const STATUSES = ['pending', 'completed', 'cancelled'];
export const OUTCOMES = ['passed', 'extended', 'failed'];

// preboarding-documents/config/index.js'teki çoklu seçim filtresinin BİREBİR aynısı — yeni bir
// filtre bileşeni İCAT EDİLMEZ (standart bileşen kuralı).
const multiSelectFilter = (label, options, valueRender) => ({
  filter: true,
  sort: true,
  filterType: 'custom',
  filterOptions: {
    defaultOp: 'in',
    display: (filterList, onChange, index, column) => {
      const current = filterList[index] && filterList[index][0]
        ? filterList[index][0].split(',').filter(Boolean)
        : [];
      return (
        <FormControl variant="standard" fullWidth>
          <InputLabel>{label}</InputLabel>
          <Select
            multiple
            value={current}
            onChange={(e) => {
              const v = e.target.value;
              const csv = Array.isArray(v) ? v.filter(Boolean).join(',') : '';
              filterList[index] = csv ? [csv] : [];
              onChange(filterList[index], index, column);
            }}
            renderValue={(selected) => selected.map((s) => (valueRender ? valueRender(s) : s)).join(', ')}
          >
            {options.map((o) => (
              <MenuItem key={o} value={o}>{valueRender ? valueRender(o) : o}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    },
  },
});

const useProbationReviewsApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 25, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const merged = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : (lastValidQueryParams.current || { pageNumber: 1, pageSize: 25 });
    lastValidQueryParams.current = merged;
    setFetching(true);
    api.get(`/v1/probation-reviews?${new URLSearchParams(merged)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 25,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const create = useCallback(async (formData) => {
    try {
      await api.post('/v1/probation-reviews', {
        employeeId: formData.employeeId,
        probationEndDate: formData.probationEndDate || null,
        notesMarkdown: formData.notesMarkdown || null,
      });
      refresh();
      return true;
    } catch { return false; }
  }, [refresh]);

  const complete = useCallback(async (id, formData) => {
    try {
      await api.post(`/v1/probation-reviews/${id}/complete`, {
        outcome: formData.outcome,
        // `extended` DIŞINDA tarih gönderilmez — backend zaten yok sayar ama gereksiz veri
        // göndermek "uzatma mıydı" sorusunu payload'a bakan biri için bulanıklaştırırdı.
        extendedToDate: formData.outcome === 'extended' ? (formData.extendedToDate || null) : null,
        notesMarkdown: formData.notesMarkdown || null,
      });
      refresh();
      return true;
    } catch { return false; }
  }, [refresh]);

  const cancel = useCallback(async (id) => {
    try { await api.post(`/v1/probation-reviews/${id}/cancel`, {}); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const columns = useMemo(() => {
    const statusLabel = (s) => t(`probationReview.status.${s}`, { defaultValue: s });
    const outcomeLabel = (s) => (s ? t(`probationReview.outcome.${s}`, { defaultValue: s }) : '');

    return [
      { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
      { name: 'employeeId', label: 'EMP', options: { filter: false, sort: false, display: 'excluded' } },
      {
        name: 'employeeFullName', label: t('probationReview.employee'),
        options: {
          customBodyRender: (v, tableMeta) => (
            <PersonCell name={v} profilePicture={data[tableMeta.rowIndex]?.employeeProfilePicture} />
          ), filter: false, sort: false },
      },
      dateFilterColumn('probationEndDate', t('probationReview.probationEndDate'), lang),
      {
        name: 'status', label: t('probationReview.statusLabel'),
        options: {
          ...multiSelectFilter(t('probationReview.statusLabel'), STATUSES, statusLabel),
          customBodyRender: (v) => statusLabel(v),
        },
      },
      {
        name: 'outcome', label: t('probationReview.outcomeLabel'),
        options: {
          ...multiSelectFilter(t('probationReview.outcomeLabel'), OUTCOMES, outcomeLabel),
          customBodyRender: (v) => outcomeLabel(v),
        },
      },
      {
        name: 'isOverdue', label: t('probationReview.isOverdue'),
        options: {
          filter: false, sort: false,
          customBodyRender: (v) => (v ? t('common.yes') : t('common.no')),
        },
      },
      dateFilterColumn('reviewedAt', t('probationReview.reviewedAt'), lang),
    ];
  }, [t, lang, data]);

  // Yeni değerlendirme açma formu. probationEndDate BOŞ bırakılabilir — backend çalışanın kendi
  // ProbationEndDate'ini kullanır (en yaygın hâl); açıkça vermek yalnız geçmişe dönük giriş için.
  const createSchema = useMemo(() => [
    {
      name: 'employeeId', label: t('probationReview.employee'), type: 'asyncSelect', required: true,
      onSearch: async (q) => {
        const params = q ? { 'firstName[like]': q, pageSize: 20 } : { pageSize: 20 };
        const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
        const r = res.data?.data; const items = Array.isArray(r) ? r : (r?.data || []);
        return items.map((e) => ({ id: e.id, name: `${e.firstName || ''} ${e.lastName || ''}`.trim(), profilePicture: e.profilePicture }));
      },
      onGetById: async (id) => {
        const res = await api.get(`/v1/employees/${id}`);
        const e = res.data?.data || {};
        return { id, name: `${e.firstName || ''} ${e.lastName || ''}`.trim(), profilePicture: e.profilePicture };
      },
    },
    { name: 'probationEndDate', label: t('probationReview.probationEndDateOptional'), type: 'date' },
    { name: 'notesMarkdown', label: t('probationReview.notes'), type: 'textarea' },
  ], [t]);

  // Karar formu. extendedToDate yalnız `extended` seçilince ANLAMLIDIR; GenericFormDrawer koşullu
  // alan desteklemediği için alan hep görünür ve yardım metni bunu söyler — backend de
  // `extended` değilken yok sayar, `extended` iken zorunlu tutar (tek doğrulama kaynağı orası).
  const completeSchema = useMemo(() => [
    {
      name: 'outcome', label: t('probationReview.outcomeLabel'), type: 'select', required: true,
      options: OUTCOMES.map((v) => ({ id: v, name: t(`probationReview.outcome.${v}`, { defaultValue: v }) })),
    },
    { name: 'extendedToDate', label: t('probationReview.extendedToDate'), type: 'date' },
    { name: 'notesMarkdown', label: t('probationReview.notes'), type: 'textarea' },
  ], [t]);

  return { fetching, data, pagination, columns, createSchema, completeSchema, refresh, create, complete, cancel };
};

export default useProbationReviewsApi;
